import type { HTMLAttributes, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import IconCrossOutlined from '@repo/monochrome-icons/icon-cross-outlined';
import cx from 'clsx';
import { Button } from '#components/button/button';
import styles from './fullscreen-base.module.css';

interface FullscreenBaseProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Replace the header.
   * @see Removes also the close button
   */
  header?: ReactNode;
  hasLayout?: boolean;
  onClose?: () => void;
}

export function FullscreenBase({
  children,
  header,
  hasLayout,
  onClose,
  ...props
}: FullscreenBaseProps): ReactNode {
  const intl = useIntl();
  const headerClass = !hasLayout ? 'close-wrapper--base' : 'close-wrapper';
  const closeBtnClass = !hasLayout ? 'close-button--base' : 'close-button';

  const renderHeader = (): ReactNode => {
    if (header) return header;
    if (!onClose) return null;
    return (
      <div className={styles[headerClass]}>
        <Button
          aria-label={intl.formatMessage({
            id: 'ui-kit.btn.close',
            defaultMessage: 'Close',
          })}
          className={styles[closeBtnClass]}
          data-testid="close-button"
          iconOnly
          onPress={onClose}
          slot="close"
          type="button"
          variant="tertiary"
        >
          <IconCrossOutlined />
        </Button>
      </div>
    );
  };

  return (
    <div
      {...props}
      className={cx(props.className, styles['fullscreen-base'])}
      data-testid="fullscreen-base"
    >
      {renderHeader()}
      {children}
    </div>
  );
}
